import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calculator, FolderOpen, Video, Image, FileText, Send, 
  LogOut, RefreshCw, Plus, Trash2, CheckCircle, Clock, AlertTriangle,
  MapPin, Home, Euro, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Work categories for chiffrage
const WORK_CATEGORIES = [
  { id: 'electricite', label: 'Électricité', icon: '⚡' },
  { id: 'plomberie', label: 'Plomberie/SDB', icon: '🚿' },
  { id: 'sols', label: 'Sols', icon: '🏠' },
  { id: 'peinture', label: 'Peinture', icon: '🎨' },
  { id: 'cuisine', label: 'Cuisine', icon: '🍳' },
  { id: 'menuiseries', label: 'Menuiseries/Fenêtres', icon: '🚪' },
  { id: 'toiture', label: 'Toiture', icon: '🏗️' },
  { id: 'facade', label: 'Façade/Ravalement', icon: '🧱' },
  { id: 'isolation', label: 'Isolation', icon: '🌡️' },
  { id: 'chauffage', label: 'Chauffage', icon: '🔥' },
  { id: 'demolition', label: 'Démolition', icon: '🔨' },
  { id: 'maconnerie', label: 'Maçonnerie', icon: '🪨' },
];

export default function ChiffreurDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estimates, setEstimates] = useState([]);
  const [notes, setNotes] = useState('');
  const [customWork, setCustomWork] = useState({ label: '', price: '', quantity: '', unit: 'forfait' });
  const [activeVideo, setActiveVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const videoRef = useRef(null);

  const chiffreurData = JSON.parse(localStorage.getItem('chiffreur_data') || '{}');
  const token = localStorage.getItem('chiffreur_token');

  useEffect(() => {
    if (!token) {
      navigate('/chiffreur');
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/chiffreur/projects`);
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0]);
        setEstimates(response.data[0].chiffreur_estimates || []);
        setNotes(response.data[0].chiffreur_notes || '');
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('chiffreur_token');
    localStorage.removeItem('chiffreur_data');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/chiffreur');
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setEstimates(project.chiffreur_estimates || []);
    setNotes(project.chiffreur_notes || '');
    setActiveVideo(null);
    setSnapshots([]);
  };

  // Capture snapshot from video
  const captureSnapshot = () => {
    const video = videoRef.current;
    if (!video) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    const currentTime = video.currentTime;
    
    const newSnapshot = {
      id: Date.now(),
      dataUrl,
      timestamp: currentTime,
      label: `Capture à ${Math.floor(currentTime / 60)}:${String(Math.floor(currentTime % 60)).padStart(2, '0')}`
    };
    
    setSnapshots(prev => [...prev, newSnapshot]);
    toast.success(`📸 Capture enregistrée à ${newSnapshot.label}`);
  };

  const removeSnapshot = (snapshotId) => {
    setSnapshots(prev => prev.filter(s => s.id !== snapshotId));
  };

  const addWorkItem = (category) => {
    const cat = WORK_CATEGORIES.find(c => c.id === category);
    if (!cat) return;
    
    const newItem = {
      id: `${category}-${Date.now()}`,
      category,
      label: cat.label,
      price: 0,
      quantity: 1,
      unit: 'forfait',
      total: 0
    };
    setEstimates([...estimates, newItem]);
  };

  const addCustomItem = () => {
    if (!customWork.label || !customWork.price) {
      toast.error('Remplissez la désignation et le prix');
      return;
    }
    
    const newItem = {
      id: `custom-${Date.now()}`,
      category: 'custom',
      label: customWork.label,
      price: parseFloat(customWork.price),
      quantity: parseFloat(customWork.quantity) || 1,
      unit: customWork.unit,
      total: parseFloat(customWork.price) * (parseFloat(customWork.quantity) || 1)
    };
    setEstimates([...estimates, newItem]);
    setCustomWork({ label: '', price: '', quantity: '', unit: 'forfait' });
  };

  const updateEstimate = (id, field, value) => {
    setEstimates(estimates.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'price' || field === 'quantity') {
          updated.total = (parseFloat(updated.price) || 0) * (parseFloat(updated.quantity) || 1);
        }
        return updated;
      }
      return item;
    }));
  };

  const removeEstimate = (id) => {
    setEstimates(estimates.filter(item => item.id !== id));
  };

  const calculateTotal = () => {
    return estimates.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const handleSubmit = async () => {
    if (estimates.length === 0) {
      toast.error('Ajoutez au moins un poste de travaux');
      return;
    }

    setSubmitting(true);
    try {
      // Include snapshots data in the submission
      const snapshotsData = snapshots.map(s => ({
        dataUrl: s.dataUrl,
        label: s.label,
        timestamp: s.timestamp
      }));
      
      await axios.post(`${API}/chiffreur/projects/${selectedProject.id}/submit`, {
        estimates,
        notes,
        snapshots: snapshotsData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chiffrage soumis pour validation !');
      setSnapshots([]);
      fetchProjects();
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">En cours</Badge>;
      case 'pending_validation':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">En attente validation</Badge>;
      case 'validated':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Validé</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400">En attente</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10 py-3 px-6 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="font-bold text-white text-lg">ESPACE CHIFFREUR</h1>
              <p className="text-xs text-emerald-400">{chiffreurData.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
              <FolderOpen className="w-3 h-3 mr-1" />
              {projects.length} dossiers
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchProjects}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-white/70 hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Project List */}
        <aside className="w-64 bg-black/20 border-r border-white/10 min-h-[calc(100vh-60px)] p-4">
          <h2 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-4">
            Dossiers assignés
          </h2>
          {projects.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-8">Aucun dossier assigné</p>
          ) : (
            <div className="space-y-2">
              {projects.map(project => (
                <button
                  key={project.id}
                  onClick={() => handleSelectProject(project)}
                  className={`w-full p-3 rounded-lg text-left transition-all ${
                    selectedProject?.id === project.id
                      ? 'bg-emerald-500/20 border border-emerald-500/40'
                      : 'bg-white/5 hover:bg-white/10 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${
                      project.chiffrage_status === 'validated' ? 'bg-emerald-400' :
                      project.chiffrage_status === 'pending_validation' ? 'bg-amber-400' :
                      'bg-blue-400'
                    }`} />
                    <span className="text-white font-mono text-xs">VIP-{project.id?.slice(-6).toUpperCase()}</span>
                  </div>
                  <p className="text-white/60 text-xs truncate">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {project.location_hint || 'France'}
                  </p>
                  <p className="text-amber-400 text-xs mt-1 font-medium">
                    Budget: {project.budget_label || project.budget}
                  </p>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {selectedProject ? (
            <div className="grid grid-cols-3 gap-6">
              {/* Left Column - Project Info & Videos */}
              <div className="space-y-6">
                {/* Project Info (ANONYMIZED) */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Home className="w-4 h-4 text-emerald-400" />
                      Chantier VIP-{selectedProject.id?.slice(-6).toUpperCase()}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Localisation</span>
                      <span className="text-white">{selectedProject.location_hint || 'France'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Budget estimé</span>
                      <span className="text-amber-400 font-semibold">
                        {selectedProject.budget_label || selectedProject.budget}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Statut chiffrage</span>
                      {getStatusBadge(selectedProject.chiffrage_status)}
                    </div>
                    {selectedProject.description && (
                      <div className="pt-2 border-t border-white/10">
                        <span className="text-white/60 text-xs block mb-1">Description des travaux</span>
                        <p className="text-white text-sm">{selectedProject.description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Confidentiality Notice */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <p className="text-amber-300 text-xs">
                    <strong>🔒 Confidentialité :</strong> Les coordonnées du client (nom, email, téléphone, adresse exacte) 
                    sont masquées pour garantir leur protection.
                  </p>
                </div>

                {/* Videos - Only show if chantier has videos */}
                {selectedProject.videos?.length > 0 && (
                  <Card className="bg-white/5 border-white/10">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white flex items-center gap-2 text-base">
                        <Video className="w-4 h-4 text-blue-400" />
                        Vidéos Diagnostic ({selectedProject.videos?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedProject.videos.map((video, idx) => (
                          <button
                            key={idx}
                            onClick={() => setActiveVideo(video)}
                            className={`w-full p-2 rounded text-left text-sm flex items-center gap-2 transition-all ${
                              activeVideo === video 
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'bg-white/5 hover:bg-white/10 text-white/70'
                            }`}
                          >
                            <Video className="w-4 h-4" />
                            {video.type || `Vidéo ${idx + 1}`}
                          </button>
                        ))}
                      </div>

                      {activeVideo && (
                        <div className="mt-4 space-y-3">
                          <video
                            ref={videoRef}
                            src={activeVideo.url}
                            controls
                            className="w-full rounded-lg"
                            crossOrigin="anonymous"
                          />
                          <Button 
                            onClick={captureSnapshot}
                            className="w-full bg-purple-600 hover:bg-purple-700"
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            📸 Capturer cette image
                          </Button>
                        </div>
                      )}
                      
                      {/* Snapshots Gallery */}
                      {snapshots.length > 0 && (
                        <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                          <h4 className="text-sm font-semibold text-purple-300 mb-2 flex items-center gap-2">
                            <Camera className="w-4 h-4" />
                            Captures ({snapshots.length})
                          </h4>
                          <div className="grid grid-cols-3 gap-2">
                            {snapshots.map((snap) => (
                              <div key={snap.id} className="relative group">
                                <img 
                                  src={snap.dataUrl} 
                                  alt={snap.label}
                                  className="w-full h-16 object-cover rounded cursor-pointer hover:opacity-80"
                                  onClick={() => window.open(snap.dataUrl, '_blank')}
                                />
                                <button
                                  onClick={() => removeSnapshot(snap.id)}
                                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ×
                                </button>
                                <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 rounded-b truncate">
                                  {snap.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Info for VIP chantiers without media */}
                {!selectedProject.videos?.length && !selectedProject.images?.length && (
                  <Card className="bg-blue-500/10 border-blue-500/30">
                    <CardContent className="p-4">
                      <p className="text-blue-300 text-sm">
                        <strong>📋 Mission :</strong> Établir une estimation détaillée des travaux 
                        à partir de la description fournie et du budget indicatif du client.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Images */}
                {selectedProject.images?.length > 0 && (
                  <Card className="bg-white/5 border-white/10">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white flex items-center gap-2 text-base">
                        <Image className="w-4 h-4 text-purple-400" />
                        Photos ({selectedProject.images.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-2">
                        {selectedProject.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img.url}
                            alt={`Photo ${idx + 1}`}
                            className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-80"
                            onClick={() => window.open(img.url, '_blank')}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right Columns - Chiffrage Form */}
              <div className="col-span-2 space-y-6">
                {/* Work Categories */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-emerald-400" />
                      Estimation Travaux
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Quick Add Buttons */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {WORK_CATEGORIES.map(cat => (
                        <Button
                          key={cat.id}
                          variant="outline"
                          size="sm"
                          onClick={() => addWorkItem(cat.id)}
                          className="text-white/70 border-white/20 hover:bg-emerald-500/20 hover:border-emerald-500/40 hover:text-emerald-300"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {cat.label}
                        </Button>
                      ))}
                    </div>

                    {/* Estimates List */}
                    {estimates.length > 0 && (
                      <div className="space-y-3 mb-6">
                        <div className="grid grid-cols-12 gap-2 text-xs text-white/40 uppercase tracking-wider pb-2 border-b border-white/10">
                          <div className="col-span-4">Désignation</div>
                          <div className="col-span-2">Prix unit.</div>
                          <div className="col-span-2">Qté</div>
                          <div className="col-span-2">Unité</div>
                          <div className="col-span-1 text-right">Total</div>
                          <div className="col-span-1"></div>
                        </div>
                        {estimates.map(item => (
                          <div key={item.id} className="grid grid-cols-12 gap-2 items-center bg-white/5 p-3 rounded-lg">
                            <div className="col-span-4">
                              <Input
                                value={item.label}
                                onChange={(e) => updateEstimate(item.id, 'label', e.target.value)}
                                className="bg-white/5 border-white/20 text-white text-sm h-8"
                              />
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number"
                                value={item.price}
                                onChange={(e) => updateEstimate(item.id, 'price', parseFloat(e.target.value) || 0)}
                                className="bg-white/5 border-white/20 text-white text-sm h-8"
                              />
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateEstimate(item.id, 'quantity', parseFloat(e.target.value) || 1)}
                                className="bg-white/5 border-white/20 text-white text-sm h-8"
                              />
                            </div>
                            <div className="col-span-2">
                              <select
                                value={item.unit}
                                onChange={(e) => updateEstimate(item.id, 'unit', e.target.value)}
                                className="w-full h-8 rounded bg-white/5 border border-white/20 text-white text-sm px-2"
                              >
                                <option value="forfait">forfait</option>
                                <option value="m²">m²</option>
                                <option value="ml">ml</option>
                                <option value="unité">unité</option>
                              </select>
                            </div>
                            <div className="col-span-1 text-right">
                              <span className="text-emerald-400 font-semibold text-sm">
                                {(item.total || 0).toLocaleString('fr-FR')} €
                              </span>
                            </div>
                            <div className="col-span-1 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeEstimate(item.id)}
                                className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Total */}
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-semibold">TOTAL TRAVAUX</span>
                        <span className="text-emerald-400 text-2xl font-bold">
                          {calculateTotal().toLocaleString('fr-FR')} €
                        </span>
                      </div>
                    </div>

                    {/* Custom Work Item */}
                    <div className="bg-white/5 rounded-lg p-4">
                      <p className="text-white/60 text-sm mb-3">Ajouter une ligne personnalisée</p>
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-4">
                          <Input
                            placeholder="Désignation"
                            value={customWork.label}
                            onChange={(e) => setCustomWork({...customWork, label: e.target.value})}
                            className="bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            placeholder="Prix €"
                            value={customWork.price}
                            onChange={(e) => setCustomWork({...customWork, price: e.target.value})}
                            className="bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            placeholder="Qté"
                            value={customWork.quantity}
                            onChange={(e) => setCustomWork({...customWork, quantity: e.target.value})}
                            className="bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <select
                            value={customWork.unit}
                            onChange={(e) => setCustomWork({...customWork, unit: e.target.value})}
                            className="w-full h-9 rounded bg-white/5 border border-white/20 text-white text-sm px-2"
                          >
                            <option value="forfait">forfait</option>
                            <option value="m²">m²</option>
                            <option value="ml">ml</option>
                            <option value="unité">unité</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <Button
                            onClick={addCustomItem}
                            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Notes */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2 text-base">
                      <FileText className="w-4 h-4 text-blue-400" />
                      Notes & Observations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Précisions techniques, points d'attention, recommandations..."
                      rows={4}
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
                    />
                  </CardContent>
                </Card>

                {/* Submit Button */}
                <div className="flex justify-end gap-3">
                  {selectedProject.chiffrage_status === 'pending_validation' ? (
                    <div className="flex items-center gap-2 text-amber-400">
                      <Clock className="w-4 h-4" />
                      En attente de validation par l'expert
                    </div>
                  ) : selectedProject.chiffrage_status === 'validated' ? (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      Chiffrage validé
                    </div>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting || estimates.length === 0}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white px-8"
                    >
                      {submitting ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Envoi...
                        </div>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Soumettre le chiffrage
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-96">
              <div className="text-center text-white/40">
                <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-40" />
                <p className="text-lg">Sélectionnez un dossier</p>
                <p className="text-sm">pour commencer le chiffrage</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
